import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPage.css';

const PrivacyPage: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <section className="privacy-hero">
          <div className="privacy-hero-row">
            <div>
              <h1>Privacy Policy</h1>
              <div className="privacy-meta">SynorLab Website — Protecting your privacy</div>
            </div>
            <div className="privacy-hero-right">
              <div className="privacy-badge">Version 1.0</div>
              <div className="privacy-updated">Last Updated: December 6, 2025</div>
            </div>
          </div>
        </section>

        <article className="privacy-card">
          <h2>1. Introduction</h2>
          <p>Welcome to SynorLab ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>synorlab.com</strong> (the "Website"). Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the Website.</p>

          <h2>2. Information We Collect</h2>
          <p>We may collect information about you in a variety of ways:</p>
          
          <h3>Automatically Collected Information</h3>
          <p>When you visit our Website, we may automatically collect certain information about your device, including:</p>
          <ul>
            <li><strong>Device Information</strong>: Device type, operating system, browser type and version;</li>
            <li><strong>Log Data</strong>: IP address, access times, pages viewed, and referring URL;</li>
            <li><strong>Cookies and Similar Technologies</strong>: Information collected through cookies, web beacons, and similar tracking technologies.</li>
          </ul>

          <h3>Information You Provide</h3>
          <p>We may collect information you voluntarily provide to us, such as:</p>
          <ul>
            <li>Contact information (name, email address) when you use our contact form;</li>
            <li>Any other information you choose to provide.</li>
          </ul>

          <h2>3. Cookies and Tracking Technologies</h2>
          <p>We use cookies and similar tracking technologies to collect and use personal information about you. Cookies are small data files stored on your device that help us improve our Website and your experience.</p>
          
          <h3>Types of Cookies We Use</h3>
          <ul>
            <li><strong>Essential Cookies</strong>: Required for the Website to function properly;</li>
            <li><strong>Analytics Cookies</strong>: Help us understand how visitors interact with our Website;</li>
            <li><strong>Advertising Cookies</strong>: Used to deliver relevant advertisements and track ad campaign performance.</li>
          </ul>

          <p>You can control cookies through your browser settings. However, disabling cookies may affect your experience on our Website.</p>

          <h2>4. Google AdSense and Third-Party Advertising</h2>
          <p>We use Google AdSense to display advertisements on our Website. Third-party vendors, including Google, use cookies to serve ads based on your prior visits to our Website or other websites.</p>
          
          <p>Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to our Website and/or other sites on the Internet. These cookies may collect information including:</p>
          <ul>
            <li>Your IP address;</li>
            <li>Browser type and settings;</li>
            <li>Information about your browsing behavior;</li>
            <li>Unique identifiers for advertising purposes.</li>
          </ul>

          <h3>Opting Out of Personalized Advertising</h3>
          <p>You may opt out of personalized advertising by:</p>
          <ul>
            <li>Visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>;</li>
            <li>Visiting <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info</a> to opt out of third-party vendor cookies;</li>
            <li>Visiting <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">Network Advertising Initiative opt-out page</a>.</li>
          </ul>

          <p>For more information about how Google uses data when you use our Website, please visit: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">How Google uses data when you use our partners' sites or apps</a>.</p>

          <h2>5. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Operate and maintain our Website;</li>
            <li>Improve and personalize your experience;</li>
            <li>Analyze usage patterns and trends;</li>
            <li>Display relevant advertisements;</li>
            <li>Respond to your inquiries and provide customer support;</li>
            <li>Comply with legal obligations.</li>
          </ul>

          <h2>6. Sharing Your Information</h2>
          <p>We may share your information in the following situations:</p>
          <ul>
            <li><strong>With Service Providers</strong>: We may share information with third-party service providers who perform services on our behalf (e.g., analytics, advertising);</li>
            <li><strong>For Legal Purposes</strong>: We may disclose information if required by law or to protect our rights;</li>
            <li><strong>With Your Consent</strong>: We may share information with your consent or at your direction.</li>
          </ul>
          <p>We do not sell, rent, or trade your personal information to third parties.</p>

          <h2>7. EU User Consent Policy (GDPR)</h2>
          <p>For users in the European Economic Area (EEA), we comply with the EU General Data Protection Regulation (GDPR). Under GDPR, you have the following rights:</p>
          <ul>
            <li><strong>Right to Access</strong>: You can request a copy of your personal data;</li>
            <li><strong>Right to Rectification</strong>: You can request correction of inaccurate data;</li>
            <li><strong>Right to Erasure</strong>: You can request deletion of your personal data;</li>
            <li><strong>Right to Restrict Processing</strong>: You can request limitation of data processing;</li>
            <li><strong>Right to Data Portability</strong>: You can request transfer of your data;</li>
            <li><strong>Right to Object</strong>: You can object to data processing for certain purposes;</li>
            <li><strong>Right to Withdraw Consent</strong>: You can withdraw consent at any time.</li>
          </ul>
          <p>We will obtain your consent before using cookies or similar technologies for advertising purposes, collecting personal data for personalized advertising, or sharing data with third-party advertising partners.</p>
          <p>To exercise these rights, please contact us using the information provided below.</p>

          <h2>8. Children's Privacy (COPPA)</h2>
          <p>Our Website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe that your child has provided us with personal information, please contact us immediately. If we discover that we have collected personal information from a child under 13 without parental consent, we will take steps to delete that information.</p>

          <h2>9. Data Security</h2>
          <p>We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee its absolute security.</p>

          <h2>10. Third-Party Links</h2>
          <p>Our Website may contain links to third-party websites (such as Google Play Store). We are not responsible for the privacy practices or content of these third-party sites. We encourage you to review the privacy policies of any third-party sites you visit.</p>

          <h2>11. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.</p>

          <h2>12. Contact Us</h2>
          <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
          <div className="privacy-contact">
            <div className="privacy-email">
              📧 <a href="mailto:synorlab.com@gmail.com">synorlab.com@gmail.com</a>
            </div>
            <div className="privacy-contact-note">We aim to respond within a reasonable time.</div>
          </div>

          <h2>13. Product-Specific Legal Documents</h2>
          <p>Each of our applications has its own privacy policy and terms of service:</p>
          <ul>
            <li><Link to="/bomb-world-classic/privacy">Bomb World: Classic Privacy Policy</Link> · <Link to="/bomb-world-classic/terms">Terms of Service</Link></li>
            <li><Link to="/infinity-brain/privacy">Infinity Brain Training Privacy Policy</Link> · <Link to="/infinity-brain/terms">Terms of Service</Link></li>
            <li><Link to="/personal-prize/privacy">Personal Prize Privacy Policy</Link> · <Link to="/personal-prize/terms">Terms of Service</Link></li>
            <li><Link to="/personal-vault/privacy">Personal Vault Privacy Policy</Link> · <Link to="/personal-vault/terms">Terms of Service</Link></li>
            <li><Link to="/personal-contact/privacy">Personal Contact Privacy Policy</Link> · <Link to="/personal-contact/terms">Terms of Service</Link></li>
            <li><Link to="/wayhome/privacy">Way Home Privacy Policy</Link> · <Link to="/wayhome/terms">Terms of Service</Link></li>
          </ul>

          <footer className="privacy-footer">
            <div>© SynorLab — All rights reserved.</div>
          </footer>
        </article>

        <div className="privacy-back">
          <Link to="/" className="privacy-back-link">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;

